import { type FC, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { noop } from 'lodash-es';

import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';
import { Icon } from '@iconik-io/uad';

import { ui } from '../../utils/helpers';

import styles from './checkbox_basic.module.css';

export type ValueType = 'checked' | 'indeterminate' | null;

export type CheckboxBasicProps = {
  id?: string;
  name?: string;
  value?: ValueType;
  onChange?: (value: ValueType) => void;
  className?: string;
  inputClassName?: string;
  uiKey?: string;
  size?: 's' | 'm';
  disabled?: boolean;
  aria?: AriaProps;
};

const sizeStyleMap = {
  s: 'size_s',
  m: 'size_m',
} as const;

const CheckboxBasic: FC<CheckboxBasicProps> = (props) => {
  const {
    value,
    onChange,
    uiKey,
    className,
    inputClassName,
    id,
    size = 'm',
    name,
    disabled,
    aria,
  } = props;
  const checkboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const element = checkboxRef.current;
    if (!element) {
      return;
    }
    switch (value) {
      case null:
        element.indeterminate = false;

        break;
      case 'indeterminate':
        element.indeterminate = true;
        break;
      case 'checked':
        element.indeterminate = false;

        break;
      default:
        break;
    }
  }, [value]);

  return (
    <div
      className={classNames(
        styles.checkbox,
        styles[sizeStyleMap[size]],
        className,
      )}
      onClick={() => {
        if (disabled) {
          return;
        }
        switch (value) {
          case null:
            onChange?.('checked');
            break;
          case 'indeterminate':
            onChange?.('checked');
            break;
          case 'checked':
            onChange?.(null);
            break;
          default:
            break;
        }
      }}
      {...ui(uiKey)}
    >
      <input
        id={id}
        name={name}
        type="checkbox"
        ref={checkboxRef}
        checked={value === 'checked'}
        disabled={disabled}
        onChange={noop}
        value={value === null ? 'unchecked' : value}
        className={classNames(inputClassName, {
          [styles.indeterminate_input]: value === 'indeterminate',
        })}
        {...ariaToReactProps(aria)}
      />
      <Icon name="checkmark" className={styles.icon} />
      <div className={styles.indeterminate} />
    </div>
  );
};

export default CheckboxBasic;
